import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, gql, useMutation } from '@apollo/client';
import {
    Header, Button, Input, TextArea, Form,
    Divider, Label, Message, Loader, Container, Icon
} from 'semantic-ui-react';

import { MediaTile } from '../../../components/MediaTile';
import { PosterAvatar } from '../../../components/PosterAvatar';
import { StreamBadge, STREAM_BADGE_FIELDS } from '../../../components/StreamBadge';
import { parseApiDate } from '../../../utils/date';
import { POST_TITLE_LIMIT, POST_DESCRIPTION_LIMIT, characterCountColour } from '../../../constants';
import { AdvertisedRoles } from '../../../components/AdvertisedRoles';
import { PollPreview } from '../../../components/PollPreview';

const COMMUNITY_GROUP_ID = 2;

// Post is a union (CommunityUpdate | CommunityPrayerRequest | LeadershipUpdate
// | LeadershipPrayerRequest | Birthday); fields must be selected per member.
const POST_REVIEW_FIELDS = `
    id
    title
    description
    type
    publishAt
    tags { id name groupId }
    ${STREAM_BADGE_FIELDS}
    poster { name photoUrl }
    images { id url contentType durationSeconds tags { id name groupId } }
`;

const GET_POST_FOR_REVIEW = gql`
     query Post($postId: Int!) {
         post(id: $postId) {
             ... on CommunityUpdate { ${POST_REVIEW_FIELDS} }
             ... on VolunteerCall { ${POST_REVIEW_FIELDS} advertisedVolunteerRoles { id title type } }
             ... on Poll { ${POST_REVIEW_FIELDS} options { id label } allowMultiple allowOther closesAt }
             ... on CommunityPrayerRequest { ${POST_REVIEW_FIELDS} }
             ... on LeadershipUpdate { ${POST_REVIEW_FIELDS} }
             ... on LeadershipPrayerRequest { ${POST_REVIEW_FIELDS} }
             ... on Birthday { ${POST_REVIEW_FIELDS} }
         }
     }
`;

const UPDATE_POST_STATUS = gql`
    mutation UpdatePostStatus($updatePostStatusId: Int!, $status: String!, $title: String, $description: String, $reason: String, $publishAt: String) {
        updatePostStatus(id: $updatePostStatusId, status: $status, title: $title, description: $description, reason: $reason, publishAt: $publishAt) {
            ... on CommunityUpdate { id }
            ... on VolunteerCall { id }
            ... on Poll { id }
            ... on CommunityPrayerRequest { id }
            ... on LeadershipUpdate { id }
            ... on LeadershipPrayerRequest { id }
            ... on Birthday { id }
        }
    }
`;

type EditableFields = { title: string; description: string };

/** `yyyy-MM-ddThh:mm` for a datetime-local input, in the browser's own zone. */
const toLocalInputValue = (date: Date) => {
    const offset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

// --- Component ---

const PostReviewView: React.FC = () => {
    const { postId } = useParams<{ postId: string }>();
    const navigate = useNavigate();
    const [isExiting, setIsExiting] = useState(false);

    const { data, loading, error } = useQuery(GET_POST_FOR_REVIEW, {
        variables: { postId: Number(postId) },
        skip: !postId
    });

    // Seeded once from the query result, then edited locally until the reviewer acts.
    const [form, setForm] = useState<EditableFields | null>(null);
    useEffect(() => {
        if (data?.post && form == null) {
            setForm({
                title: data.post.title || '',
                description: data.post.description || '',
            });
        }
    }, [data, form]);

    const [updateStatus, { loading: updating }] = useMutation(UPDATE_POST_STATUS);

    // Rejection reason, and the reschedule offered when the stored time has already passed.
    const [rejecting, setRejecting] = useState(false);
    const [reason, setReason] = useState('');
    const [rescheduleTo, setRescheduleTo] = useState<string | null>(null);

    /**
     * Memoized logic to categorize tags and validate image tagging consistency
     */
    const { communities, postTags, warnings } = useMemo(() => {
        if (!data?.post) return { communities: [], postTags: [], warnings: [] };

        const post = data.post;
        const comms = post.tags.filter((t: any) => t.groupId === COMMUNITY_GROUP_ID);
        const tags = post.tags.filter((t: any) => t.groupId !== COMMUNITY_GROUP_ID);

        const validationWarnings: string[] = [];

        post.images.forEach((img: any, index: number) => {
            const imgGroupIds = new Set(img.tags.map((t: any) => t.groupId as number));

            comms.forEach((c: any) => {
                if (!imgGroupIds.has(c.groupId as number)) {
                    validationWarnings.push(`Image #${index + 1} is not tagged for "${c.name}".`);
                }
            });
        });

        return { communities: comms, postTags: tags, warnings: validationWarnings };
    }, [data]);

    const handleAction = async (status: 'APPROVED' | 'REJECTED') => {
        if (!form) return;
        try {
            await updateStatus({
                variables: {
                    updatePostStatusId: Number(postId),
                    status,
                    title: form.title,
                    description: form.description,
                    reason: status === 'REJECTED' ? (reason.trim() || null) : null,
                    // Only ever sent when the reviewer has actually chosen a new time. Left
                    // off, the server keeps its old behaviour of publishing a stale schedule
                    // immediately — which is what the warning above is telling them.
                    publishAt: status === 'APPROVED' ? rescheduleTo : null,
                }
            });
            setIsExiting(true);
            setTimeout(() => navigate('/posts/reviews'), 500);
        } catch (err) {
            console.error("Review action failed", err);
        }
    };

    if (loading || !form) return <Loader active inline='centered' style={{ marginTop: '5em' }}>Loading Post...</Loader>;
    if (error || !data?.post) return <Message error content="Could not load post details." />;

    const { post } = data;
    const overLimit = form.title.length > POST_TITLE_LIMIT || form.description.length > POST_DESCRIPTION_LIMIT;
    const dueAt = parseApiDate(post.publishAt);
    // Null publishAt means "no time was ever set", which the server also publishes immediately
    // on approval — same surprise, so it gets the same warning.
    const scheduledInThePast = dueAt == null || dueAt.getTime() < Date.now();

    return (
        <Container
            text
            style={{
                padding: '3em 0em',
                transition: 'opacity 0.5s ease-in-out',
                opacity: isExiting ? 0 : 1
            }}
        >
            <Label basic color="grey" size="small" style={{ marginBottom: '1em' }}>{post.type}</Label>
            <AdvertisedRoles roles={post.advertisedVolunteerRoles} />
            <PollPreview post={post} />

            {/* Who receives this. Sits above the tags because approving the post approves the
                audience, and since Phase 6 the stream decides that, not the tags. */}
            <div style={{ marginBottom: '10px' }}>
                <StreamBadge stream={post.stream} size="small" />
            </div>

            {/* Communities Display */}
            {communities.length > 0 && (
                <div style={{ marginBottom: '10px' }}>
                    <span style={{ marginRight: '10px', fontWeight: 'bold', color: '#666' }}>Communities:</span>
                    {communities.map((c: any) => (
                        <Label key={c.id} color="purple" size="small" style={{ marginBottom: '5px' }}>
                            <Icon name="users" /> {c.name}
                        </Label>
                    ))}
                </div>
            )}

            {/* Post Standard Tags */}
            {postTags.length > 0 && (
                <div style={{ marginBottom: '1.5em' }}>
                    {postTags.map((t: any) => (
                        <Label key={t.id} basic color="teal" size="tiny" style={{ marginRight: '5px' }}>
                            #{t.name}
                        </Label>
                    ))}
                </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '2em', color: '#666' }}>
                <PosterAvatar photoUrl={post.poster.photoUrl} size={28} />
                <span style={{ marginLeft: '0.8em' }}>
                    <strong>{post.poster.name}</strong> • {parseApiDate(post.publishAt)?.toLocaleString()}
                </span>
            </div>

            {/* Warning Message Box */}
            {warnings.length > 0 && (
                <Message warning icon>
                    <Icon name="warning sign" />
                    <Message.Content>
                        <Message.Header>Tag Mismatch Detected</Message.Header>
                        <ul style={{ marginTop: '5px' }}>
                            {warnings.map((w, i) => (
                                <li key={i}>{w} Are you sure you want to send this post out?</li>
                            ))}
                        </ul>
                    </Message.Content>
                </Message>
            )}

            {/* Image Gallery with Conditional Red Border */}
            {post.images.length > 0 && (
                <div style={{
                    display: 'flex',
                    overflowX: 'auto',
                    gap: '12px',
                    paddingBottom: '15px',
                    marginBottom: '2em'
                }}>
                    {post.images.map((img: any, index: number) => {
                        const postGroupIds: number[] = communities.map((c: any) => c.groupId);
                        const imgGroupIds: number[] = img.tags.map((t: any) => t.groupId);

                        // Explicitly typing 'id' as number to fix the implicit any error
                        const isMissingTags = postGroupIds.some((id: number) => !imgGroupIds.includes(id));

                        return (
                            <div key={img.id} style={{ position: 'relative', flex: '0 0 auto' }}>
                                <MediaTile
                                    url={img.url}
                                    contentType={img.contentType}
                                    durationSeconds={img.durationSeconds}
                                    rounded
                                    bordered
                                    controls
                                    style={{
                                        height: '220px',
                                        objectFit: 'cover',
                                        border: isMissingTags ? '3px solid #db2828' : '1px solid #ddd'
                                    }}
                                />
                                {isMissingTags && (
                                    <Label color="red" corner="left" icon="warning" />
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            <Form>
                <Form.Field required>
                    <label>Title</label>
                    <Input
                        value={form.title}
                        disabled={updating || isExiting}
                        onChange={(_, { value }) => setForm(f => f && ({ ...f, title: value }))}
                    />
                    <div style={{ textAlign: 'right', fontSize: '0.8em', color: characterCountColour(form.title.length, POST_TITLE_LIMIT) }}>
                        {form.title.length} / {POST_TITLE_LIMIT}
                    </div>
                </Form.Field>

                <Form.Field required={post.type !== 'POLL'}>
                    <label>{post.type === 'POLL' ? 'More detail (optional)' : 'Description'}</label>
                    <TextArea
                        rows={6}
                        value={form.description}
                        disabled={updating || isExiting}
                        onChange={(_, { value }) => setForm(f => f && ({ ...f, description: value as string }))}
                    />
                    <div style={{ textAlign: 'right', fontSize: '0.8em', color: characterCountColour(form.description.length, POST_DESCRIPTION_LIMIT) }}>
                        {form.description.length} / {POST_DESCRIPTION_LIMIT}
                    </div>
                </Form.Field>
            </Form>

            <Divider style={{ margin: '4em 0 2em 0' }} />

            {/* A schedule that has already passed used to be rewritten to "now" silently, so
                clearing a backlog blasted last week's post to everybody the moment it was
                approved. Say so, and offer a new time, before they press the button. */}
            {scheduledInThePast && (
                <Message warning icon>
                    <Icon name="clock outline" />
                    <Message.Content>
                        <Message.Header>This was scheduled for the past</Message.Header>
                        <p>
                            It was due {parseApiDate(post.publishAt)?.toLocaleString()}. Approving
                            it now publishes it immediately and notifies everyone, unless you pick
                            a new time.
                        </p>
                        <Form>
                            <Form.Field>
                                <label>Publish at</label>
                                <Input
                                    type="datetime-local"
                                    value={rescheduleTo ?? ''}
                                    min={toLocalInputValue(new Date())}
                                    onChange={(_, { value }) => setRescheduleTo(value || null)}
                                />
                            </Form.Field>
                            {rescheduleTo && (
                                <Button
                                    basic
                                    size="tiny"
                                    content="Publish immediately instead"
                                    onClick={() => setRescheduleTo(null)}
                                />
                            )}
                        </Form>
                    </Message.Content>
                </Message>
            )}

            {rejecting && (
                <Message negative>
                    <Message.Header>Why are you rejecting this?</Message.Header>
                    <p>
                        The author sees this on their own posts page. Rejecting without a reason
                        tells them only that it was turned down.
                    </p>
                    <Form>
                        <TextArea
                            rows={3}
                            placeholder="e.g. Please add the date and the centre's name."
                            value={reason}
                            onChange={(_, { value }) => setReason(String(value ?? ''))}
                        />
                    </Form>
                    <div style={{ display: 'flex', gap: '10px', marginTop: '1em' }}>
                        <Button
                            basic
                            content="Cancel"
                            disabled={updating || isExiting}
                            onClick={() => { setRejecting(false); setReason(''); }}
                        />
                        <Button
                            negative
                            content="Reject post"
                            loading={updating}
                            disabled={isExiting}
                            onClick={() => handleAction('REJECTED')}
                        />
                    </div>
                </Message>
            )}

            <div style={{ display: 'flex', gap: '20px' }}>
                <Button
                    basic
                    color="red"
                    size="huge"
                    fluid
                    icon="trash"
                    labelPosition="left"
                    content="Reject"
                    loading={updating && rejecting}
                    disabled={isExiting || rejecting}
                    onClick={() => setRejecting(true)}
                />
                <Button
                    positive
                    size="huge"
                    fluid
                    icon="checkmark"
                    labelPosition="left"
                    content={rescheduleTo
                        ? 'Approve & schedule'
                        : (warnings.length > 0 ? 'Approve Anyway' : 'Approve')}
                    loading={updating && !rejecting}
                    disabled={isExiting || rejecting || overLimit || !form.title || (!form.description && post.type !== 'POLL')}
                    onClick={() => handleAction('APPROVED')}
                />
            </div>
        </Container>
    );
};

export { PostReviewView };
